import { fmtDateTime, URGENCY_META } from "./meta";

// In-app resurfacing logic (local timezone). No external notifications.

export function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfTomorrow() {
  const d = startOfToday();
  d.setDate(d.getDate() + 1);
  return d;
}

export function isActive(e) {
  return e.status === "ACTIVE";
}

// Past its suggested time and still open.
export function isOverdue(e) {
  if (!isActive(e) || !e.suggested_datetime) return false;
  return new Date(e.suggested_datetime) < startOfToday();
}

// Landing today: a cue dated today, OR an urgent item with no specific time.
export function isDueToday(e) {
  if (!isActive(e)) return false;
  if (e.suggested_datetime) {
    const d = new Date(e.suggested_datetime);
    return d >= startOfToday() && d < startOfTomorrow();
  }
  return e.urgency === "NOW" || e.urgency === "TODAY";
}

// Split active entries into the two resurfacing buckets (overdue takes priority).
export function getResurfacing(entries) {
  const overdue = [];
  const dueToday = [];
  for (const e of entries) {
    if (isOverdue(e)) overdue.push(e);
    else if (isDueToday(e)) dueToday.push(e);
  }
  const byTime = (a, b) =>
    new Date(a.suggested_datetime || a.created_at) - new Date(b.suggested_datetime || b.created_at);
  overdue.sort(byTime);
  dueToday.sort(byTime);
  return { overdue, dueToday };
}

// ---- Shared deterministic active-load model (single source of truth) ----------
// Every top-level surface (Capture headline, Inbox, Ahead, Daily nudge logic) should speak
// from this. DELETED (Recently Deleted) never reaches the client — the /entries API filters
// status != DELETED — and DONE is excluded by isActive(), so neither can leak into any
// active/attention/held count. Notes & journals are reference, not load.
//
// Today state is split into NON-OVERLAPPING buckets by priority — attention > review >
// handoff > held — so each item is spoken for exactly once and "held" can never overstate
// completeness by swallowing things that still need a check or were handed over.
function _commonName(names) {
  if (!names.length) return null;
  const freq = {};
  for (const n of names) freq[n] = (freq[n] || 0) + 1;
  return Object.keys(freq).sort((a, b) => freq[b] - freq[a])[0];
}

export function activeLoad(entries, scope = "me") {
  const list = (entries || []).filter((e) => {
    if (!isActive(e)) return false; // excludes DELETED + DONE
    if (e.type === "NOTE" || e.type === "JOURNAL") return false;
    if (scope === "me" && e.owner && e.owner !== "me") return false;
    return true;
  });
  const { overdue, dueToday } = getResurfacing(list);
  const attentionIds = new Set([...overdue, ...dueToday].map((e) => e.id));
  // review: active needs_review NOT already demanding attention today.
  const reviewItems = list.filter((e) => e.needs_review && !attentionIds.has(e.id));
  const reviewIds = new Set(reviewItems.map((e) => e.id));
  // handed to you: active items a household member assigned to ME (creator !== me), not already
  // counted as attention/review. Uses only the existing handoff fields — no read/open signals.
  const handoffItems = list.filter(
    (e) => e.is_handoff && e.owner === "me" && !attentionIds.has(e.id) && !reviewIds.has(e.id),
  );
  const handoffIds = new Set(handoffItems.map((e) => e.id));
  const held = list.filter(
    (e) => !attentionIds.has(e.id) && !reviewIds.has(e.id) && !handoffIds.has(e.id),
  );
  return {
    active: list.length,
    overdue: overdue.length,
    dueToday: dueToday.length,
    attention: overdue.length + dueToday.length,
    review: reviewItems.length,
    handoff: handoffItems.length,
    handoffName: _commonName(handoffItems.map((e) => e.creator_name).filter(Boolean)),
    held: held.length,
  };
}

// ---- Context-aware return v1: "Worth a look" (deterministic, horizon-window pre-surface) ----
// Brings a HELD horizon item ("this weekend"/"this week") back into view just before its window,
// for items that have no concrete datetime and so are invisible to the Daily nudge. No AI, no
// behavioural/seen/dwell signals — only item fields + the current local weekday.
const WORTH_A_LOOK_CAP = 3;

function _windowFor(phrase) {
  const p = (phrase || "").toLowerCase();
  if (/weekend/.test(p)) return "weekend";
  if (/\bweek\b/.test(p)) return "week";
  return null;
}

const WORTH_A_LOOK_WINDOW_DAYS = 14;

// A natural-language WINDOW item ("before September", "the end of the month") is due back
// gently when its window end is within the next 14 days. Windows carry no exact date and
// must NOT be gated by the weekday horizon rules — they have their own bounded end.
function _windowEndSoon(e, now) {
  if (e.timing_status !== "window" || !e.timing_window_end) return false;
  const end = new Date(e.timing_window_end).getTime();
  if (Number.isNaN(end)) return false;
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  return end >= startOfDay.getTime() && end <= now.getTime() + WORTH_A_LOOK_WINDOW_DAYS * 86400000;
}

// End of the current week/weekend = upcoming Sunday 23:59 local (today if it's Sunday).
function endOfWeek(now) {
  const d = new Date(now);
  const daysToSun = (7 - d.getDay()) % 7;
  d.setDate(d.getDate() + daysToSun);
  d.setHours(23, 59, 59, 0);
  return d;
}

// "Not now" target: the sooner of (end of the named window) and (7 days out).
export function returnSnoozeUntil(now = new Date()) {
  const t = Math.min(endOfWeek(now).getTime(), now.getTime() + 7 * 86400000);
  return new Date(t).toISOString();
}

export function worthALook(entries, now = new Date(), load = null) {
  const ld = load || activeLoad(entries || [], "me");
  if (ld.attention > 0) return [];                 // a busy day belongs to the headline
  const dow = now.getDay();                         // 0 Sun … 6 Sat
  const weekendImminent = dow === 4 || dow === 5 || dow === 6;  // Thu/Fri/Sat
  const weekImminent = dow === 1 || dow === 2;      // Mon/Tue
  const nowMs = now.getTime();

  const eligible = (entries || []).filter((e) => {
    if (!isActive(e)) return false;                 // excludes DONE + DELETED
    if (e.type === "NOTE" || e.type === "JOURNAL") return false;
    if (e.is_sensitive) return false;
    if (e.owner && e.owner !== "me") return false;
    if (e.suggested_datetime) return false;         // dated -> Daily nudge owns it
    if (e.urgency === "NOW" || e.urgency === "TODAY") return false;  // belongs to attention
    if (e.return_snoozed_until && new Date(e.return_snoozed_until).getTime() > nowMs) return false;
    // Natural-language windows: surface when the window end is within 14 days, no weekday gate.
    if (e.timing_status === "window") return _windowEndSoon(e, now);
    if (e.timing_status !== "horizon") return false;
    const win = _windowFor(e.timing_phrase);
    if (win === "weekend") return weekendImminent;
    if (win === "week") return weekImminent;
    return false;
  });

  return eligible
    .map((e) => ({ ...e, _window: e.timing_status === "window" ? "window" : _windowFor(e.timing_phrase) }))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))   // oldest first
    .slice(0, WORTH_A_LOOK_CAP);
}


// Anchor-group return: when a dated anchor is within 14 days and still has open, unsnoozed
// dependents, surface them together. Deterministic; reuses persisted anchor_entry_id links.
export function worthALookGroups(entries, now = new Date(), load = null) {
  const ld = load || activeLoad(entries || [], "me");
  if (ld.attention > 0) return [];
  const nowMs = now.getTime();
  const todayKey = new Date(now).toDateString();

  // group dependents by their anchor
  const deps = {};
  (entries || []).forEach((e) => {
    if (e.anchor_entry_id) (deps[e.anchor_entry_id] = deps[e.anchor_entry_id] || []).push(e);
  });

  const cards = [];
  for (const anchor of entries || []) {
    const linked = deps[anchor.id];
    if (!linked || !linked.length) continue;
    if (!isActive(anchor) || (anchor.owner && anchor.owner !== "me")) continue;
    if (!anchor.suggested_datetime) continue;
    if (anchor.return_snoozed_until && new Date(anchor.return_snoozed_until).getTime() > nowMs) continue;
    const dt = new Date(anchor.suggested_datetime);
    const days = (dt.getTime() - nowMs) / 86400000;
    if (days <= 0 || days > 14) continue;                 // not past, within 14 days
    if (dt.toDateString() === todayKey) continue;          // due today -> Daily nudge owns it

    const open = linked.filter((d) =>
      isActive(d) && (!d.owner || d.owner === "me") &&
      !(d.return_snoozed_until && new Date(d.return_snoozed_until).getTime() > nowMs)
    );
    if (!open.length) continue;
    cards.push({ anchor, dependents: open.slice(0, 3), date: dt });
  }
  return cards;
}

// Card-level "Not now": snooze the group return on the anchor (sooner of anchor date and +7d).
export function anchorGroupSnoozeUntil(anchor, now = new Date()) {
  const dt = anchor.suggested_datetime ? new Date(anchor.suggested_datetime).getTime() : Infinity;
  return new Date(Math.min(dt, now.getTime() + 7 * 86400000)).toISOString();
}


const _NUM_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve"];
const _count = (n) => (_NUM_WORDS[n] ? _NUM_WORDS[n] : String(n));
const _cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Deterministic, state-aware Capture headline derived LIVE from activeLoad — no AI, no cached
// or baked-in counts, so it can never contradict Inbox/Ahead. Calm and factual, never urgent.
// Speaks from the unified Today state (attention > review > handoff > held), mentions at most
// three active categories, and never mentions Worth a look — that has its own card.
export function captureHeadline(load, name, now = new Date()) {
  const hour = now.getHours();
  const tod = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const first = (name || "").trim().split(" ")[0];
  const hello = first ? `Good ${tod}, ${first}.` : `Good ${tod}.`;

  const A = load.attention || 0;
  const R = load.review || 0;
  const H = load.handoff || 0;
  const nm = load.handoffName || "Your partner";

  const attnLead = `${_cap(_count(A))} ${A === 1 ? "thing needs" : "things need"} your attention today`;
  const attnShort = `${_cap(_count(A))} ${A === 1 ? "thing needs" : "things need"} attention`;
  const revLead = `${_cap(_count(R))} ${R === 1 ? "thing needs" : "things need"} a quick check`;
  const revMore = `${_cap(_count(R))} more need${R === 1 ? "s" : ""} a quick check`;
  const revShort = `${_count(R)} need${R === 1 ? "s" : ""} a quick check`;
  const handed = H === 1 ? `${nm} handed you one thing` : `${nm} has ${_count(H)} things waiting for you`;
  const waiting = H === 1 ? `${nm} has one thing waiting for you` : `${nm} has ${_count(H)} things waiting for you`;
  const handShort = H === 1 ? `${nm} has one waiting` : `${nm} has ${_count(H)} waiting`;
  const held = " The rest is held.";

  if (A && R && H) {
    // three active categories: compress and drop the held closer to stay short on mobile.
    return `${hello} ${attnShort}, ${revShort}, and ${handShort}.`;
  }
  if (A && R) return `${hello} ${attnLead}. ${revMore}.${held}`;
  if (A && H) return `${hello} ${attnLead}, and ${waiting}.${held}`;
  if (R && H) return `${hello} ${revLead}, and ${handed}.${held}`;
  if (A) return `${hello} ${attnLead}.${held}`;
  if (R) return `${hello} ${revLead}.${held}`;
  if (H) return `${hello} ${handed}.${held}`;
  if (load.held > 0) {
    return `${hello} Nothing needs your attention today. The rest is held.`;
  }
  const when = tod === "evening" ? "tonight" : "right now";
  return `${hello} Nothing needs your attention ${when}.`;
}

// True when the Capture headline copy already names review / quick-check state, so the
// "N items to review" chip would be a duplicate signal and should be suppressed.
export function headlineRepresentsReview(headline) {
  return /quick check/i.test(headline || "");
}

// Later today: 6pm today (or +2h if already evening). Tomorrow morning: 9am tomorrow.
// This week: 3 days out at 9am.
export function snoozeTargets() {
  const now = new Date();
  const laterToday = new Date();
  if (now.getHours() < 18) laterToday.setHours(18, 0, 0, 0);
  else laterToday.setTime(now.getTime() + 2 * 3600 * 1000);
  const tomorrowMorning = new Date();
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(9, 0, 0, 0);
  const thisWeek = new Date();
  thisWeek.setDate(thisWeek.getDate() + 3);
  thisWeek.setHours(9, 0, 0, 0);
  return [
    { key: "later_today", label: "Later today", iso: laterToday.toISOString() },
    { key: "tomorrow", label: "Tomorrow morning", iso: tomorrowMorning.toISOString() },
    { key: "this_week", label: "This week", iso: thisWeek.toISOString() },
  ];
}

export function relativeDue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.round((startOfToday() - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days > 1) return `${days} days overdue`;
  if (days === 1) return "Yesterday";
  if (days === 0) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return "";
}

// Client-side mirror of backend urgency reconciliation (local timezone).
export function urgencyFromDatetime(iso) {
  if (!iso) return "LATER";
  const day = new Date(iso);
  day.setHours(0, 0, 0, 0);
  const dd = Math.round((day - startOfToday()) / 86400000);
  if (dd <= 0) return "TODAY";
  if (dd <= 7) return "THIS_WEEK";
  return "LATER";
}

// What will happen to this entry's resurfacing, stated plainly for capture time.
// tone: 'overdue' | 'today' | 'future' | 'none'
export function cueStatus(entry) {
  const dt = entry.suggested_datetime;
  if (dt) {
    const d = new Date(dt);
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (d < startOfToday()) {
      return { tone: "overdue", label: `Overdue · ${relativeDue(dt)}. Surfacing now.` };
    }
    if (d < startOfTomorrow()) {
      return { tone: "today", label: `Today, ${time}. Surfaces as Due today.` };
    }
    return { tone: "future", label: `${fmtDateTime(dt)}. I'll remind you on the day.` };
  }
  if (entry.timing_status === "window") {
    const ph = entry.timing_phrase || "a window";
    return { tone: "window", label: `Held for ${ph}. No exact date needed.` };
  }
  if (entry.timing_status === "horizon") {
    const ph = (entry.timing_phrase || "").toLowerCase();
    const label = ph.includes("weekend") ? "This weekend. No exact time." : "This week. No exact time.";
    return { tone: "horizon", label };
  }
  if (entry.timing_status === "ambiguous") {
    const ph = entry.timing_phrase ? `“${entry.timing_phrase}”` : "a timing clue";
    return { tone: "ambiguous", label: `Timing clue found — ${ph}. Choose when this comes back.` };
  }
  if (entry.urgency === "NOW" || entry.urgency === "TODAY") {
    return { tone: "today", label: "No exact time. Surfaces as Due today." };
  }
  if (entry.type === "LATER") {
    return { tone: "none", label: "Someday. No date yet." };
  }
  const u = (URGENCY_META[entry.urgency] && URGENCY_META[entry.urgency].label) || "Later";
  return { tone: "none", label: `No time cue. Saved as ${u}.` };
}

